/**
 * OptOut Service — API layer for WhatsApp campaign opt-out management.
 *
 * All endpoints require auth (httpOnly cookie).
 * Phone numbers are E.164 format without '+' (e.g. "919021522219").
 * Category is the template category: 'marketing', 'utility', 'authentication', or null for all.
 */
import axiosConfig from '../config/axiosConfig';

const OptOutService = {
  /**
   * List opted-out contacts (paginated).
   * GET /optout/list?page=1&limit=50&search=&category=
   */
  listOptedOut: async ({ page = 1, limit = 50, search = '', category = '' } = {}) => {
    const params = { page, limit, search };
    if (category) params.category = category;
    const response = await axiosConfig.get('/optout/list', { params });
    return response.data;
  },

  /**
   * Check if a phone number is opted out.
   * POST /optout/check   { phoneNumber, category? }
   * Returns: { isOptedOut, optedOutCategories: ['marketing', ...] }
   */
  checkOptOut: async (phoneNumber, category = null) => {
    const body = { phoneNumber };
    if (category) body.category = category;
    const response = await axiosConfig.post('/optout/check', body);
    return response.data;
  },

  /**
   * Manually opt-out a phone number (admin action).
   * POST /optout/opt-out   { phoneNumber, category? }
   */
  optOut: async (phoneNumber, category = null) => {
    const body = { phoneNumber };
    if (category) body.category = category;
    const response = await axiosConfig.post('/optout/opt-out', body);
    return response.data;
  },

  /**
   * Re-subscribe (opt-in) a phone number.
   * POST /optout/opt-in   { phoneNumber, category? }
   * Omit category to opt-in to ALL categories.
   */
  optIn: async (phoneNumber, category = null) => {
    const body = { phoneNumber };
    if (category) body.category = category;
    const response = await axiosConfig.post('/optout/opt-in', body);
    return response.data;
  },

  /**
   * Get the count of opted-out users who would be skipped for a campaign.
   * POST /optout/campaign-skip-count   { campaignId }
   * Now category-aware — only counts opt-outs matching the campaign's template category.
   */
  getCampaignSkipCount: async (campaignId) => {
    const response = await axiosConfig.post('/optout/campaign-skip-count', {
      campaignId,
    });
    return response.data;
  },
};

export default OptOutService;
